import React from "react";
import { Flex, Form, Input, Select, Spin } from "antd";
import { observer } from "mobx-react-lite";
import { trimRequired } from "src/common/validationRules/trimRequired";
import { AttrTypeName } from "src/types/AttrType";
import { FormObjectSection } from "src/components/Forms/FormObjectSection";
import { ZUid } from "src/types/zUid";
import { ZTechpackSettings } from "../TechpackTypes";
import {
  TechpackObjType,
  techpackSettingsStore,
} from "./TechpackSettingsStore";
import { ObjectServicesSelector } from "./ObjectServicesSelector";

interface PropsTechpackSettings {
  typesMap: Record<number, string>;
  objectId: ZUid;
  serviceObjectId: ZUid;
}

const fieldKey = (key: keyof ZTechpackSettings) => key;

export const TechpackSettings: React.FC<PropsTechpackSettings> = observer(
  ({ typesMap, objectId, serviceObjectId }) => {
    React.useEffect(() => {
      techpackSettingsStore.init(typesMap, objectId);
    }, [serviceObjectId, objectId]);
    const sectionsStore = techpackSettingsStore.formSectionsStore;
    const { loading, attrsMap, loadingAttrs } = sectionsStore;

    return (
      <Spin spinning={loading}>
        <Flex gap={12} vertical>
          <FormObjectSection<ZTechpackSettings, TechpackObjType>
            store={sectionsStore}
            objKey={["techpackObjectId"]}
            objType="techpack"
            required
            objLabel="Объект для формирования Techpack"
            attrs={[
              {
                attrKey: ["exportFileNameAttributeId"],
                attrLabel: "Наименование выгружаемого файла",
                attrType: AttrTypeName.article,
                required: false,
              },
              {
                attrKey: ["createArchiveAttrId"],
                attrLabel: "Формирование архива",
                attrType: AttrTypeName.boolean,
                required: false,
              },
            ]}
          />
          <Form.Item
            label="URL для формирования"
            name={fieldKey("techpackUrl")}
            rules={[trimRequired()]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item
            label="Артикул модели"
            name={fieldKey("articleAttributeId")}
            rules={[{ required: true }]}
          >
            <Select
              options={sectionsStore.makeOptionsByType(
                AttrTypeName.article,
                attrsMap.model ?? [],
              )}
              showSearch
              optionFilterProp="filterProp"
              allowClear
              loading={loadingAttrs.has("model")}
            />
          </Form.Item>

          <FormObjectSection<ZTechpackSettings, TechpackObjType>
            store={sectionsStore}
            objKey={["supplierObjectId"]}
            objType="supplier"
            required
            objLabel="Объект поставщика"
            attrs={[
              {
                attrKey: ["supplierNameAttributeId"],
                attrLabel: "Имя поставщика",
                attrType: AttrTypeName.string,
              },
            ]}
          />
          <Form.Item
            label="Бизнес-сервис BOM"
            name={fieldKey("bomBusinessServiceId")}
          >
            <ObjectServicesSelector objectId={objectId} />
          </Form.Item>
          <Form.Item
            label="Связь с Sketch position (список зависимых объектов)"
            name={fieldKey("sketchPositionAttrId")}
          >
            <Select
              options={sectionsStore.makeOptionsByType(
                AttrTypeName.childEntities,
                attrsMap.model ?? [],
              )}
              showSearch
              optionFilterProp="filterProp"
              allowClear
              mode="multiple"
              loading={loadingAttrs.has("model")}
            />
          </Form.Item>
          <Form.Item label="Pattern" name={fieldKey("patternFileAttrId")}>
            <Select
              options={[AttrTypeName.file, AttrTypeName.childEntities].flatMap(
                (type) =>
                  sectionsStore.makeOptionsByType(type, attrsMap.model ?? []),
              )}
              showSearch
              optionFilterProp="filterProp"
              allowClear
              mode="multiple"
              loading={loadingAttrs.has("model")}
            />
          </Form.Item>
        </Flex>
      </Spin>
    );
  },
);
